import { useEffect, useRef, useState } from "react";
import { Mic, Square, Settings as SettingsIcon, List } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { BAR_COUNT, COLORS, formatTime, useAudioRecorder } from "../hooks/useAudioRecorder";
import { useApiClient } from "../api/client";
import InstallPrompt from "../components/InstallPrompt";
import AuthChip from "../components/AuthChip";
import TutorialCards from "../components/TutorialCards";
import WhatIsHandInvoice from "../components/WhatIsHandInvoice";
import BrandMark from "../components/BrandMark";
import AppFooter from "../components/AppFooter";
import { JsonLd } from "../hooks/usePageMeta";
import { softwareApplicationLd, howToLd } from "../seo";

// NOTE: Invoice numbers are now server-issued (YYYYMMDD-NNN, assigned at
// finalize). The Home screen carries no number — the real one is shown on
// the Success screen. A "LISTENING" indicator appears only while recording.

export default function Home() {
  const rec = useAudioRecorder();
  const { status, elapsedMs, error, levels, start, stop, reset, audioBlob, blobSize, mimeType } = rec;
  const navigate = useNavigate();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const api = useApiClient();

  const [sizeDisplay, setSizeDisplay] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const loggedRef = useRef(false);
  const uploadStartedRef = useRef(false);

  // Console-log the blob, capture size, and upload to /api/process once
  // per recording.
  useEffect(() => {
    if (status === "recorded" && audioBlob && !loggedRef.current) {
      loggedRef.current = true;
      setSizeDisplay(blobSize);
      // eslint-disable-next-line no-console
      console.log("[Ledger] recording complete", {
        blob: audioBlob,
        size: blobSize,
        mimeType,
        durationMs: elapsedMs,
      });
    }
    if (status === "idle") {
      loggedRef.current = false;
      uploadStartedRef.current = false;
      setUploadError("");
      setIsUploading(false);
    }
  }, [status, audioBlob, blobSize, mimeType, elapsedMs]);

  // Trigger upload exactly once per recorded blob.
  useEffect(() => {
    if (status !== "recorded" || !audioBlob || uploadStartedRef.current) return;
    uploadStartedRef.current = true;
    setIsUploading(true);
    setUploadError("");

    const fd = new FormData();
    // Use a sensible filename + extension based on mime type for the multipart part.
    const ext = (mimeType || "").includes("mp4")
      ? "m4a"
      : (mimeType || "").includes("ogg")
        ? "ogg"
        : "webm";
    fd.append("audio", audioBlob, `handinvoice-job-${Date.now()}.${ext}`);

    api
      .post("/process", fd)
      .then((r) => {
        const sid = r?.data?.session_id;
        if (!sid) throw new Error("Server returned no session_id.");
        // eslint-disable-next-line no-console
        console.log("[Ledger] /api/process →", r.data);
        navigate(`/review/${sid}`);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.detail || err?.message || "Upload failed.";
        setUploadError(String(msg));
        setIsUploading(false);
      });
  }, [api, status, audioBlob, mimeType, navigate]);

  const isIdle = status === "idle";
  const isRecording = status === "recording";
  const isRecorded = status === "recorded";

  const onPrimaryTap = () => {
    if (isIdle) start();
    else if (isRecording) stop();
  };

  const onRecordAgain = () => {
    reset();
  };

  return (
    <div
      data-testid="home-page"
      style={{
        minHeight: "100dvh",
        backgroundColor: COLORS.bg,
        color: "#f4f1ea",
        display: "flex",
        flexDirection: "column",
        padding: "22px 22px 20px",
        boxSizing: "border-box",
        fontFamily: "'Inter', system-ui, sans-serif",
        WebkitTapHighlightColor: "transparent",
        userSelect: "none",
        overflow: "hidden",
      }}
    >
      <GlobalKeyframes />

      {/* Top bar */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          data-testid="home-brand"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <BrandMark size="header" testId="home-brand-dot" />
          <span
            style={{
              fontFamily: "'Instrument Serif', Georgia, serif",
              fontStyle: "italic",
              fontSize: 22,
              color: "#f4f1ea",
              letterSpacing: "0.01em",
              lineHeight: 1,
            }}
          >
            HandInvoice
          </span>
        </div>
        {(
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            {!isRecording && <AuthChip />}
            {!isRecording && authLoaded && isSignedIn && (
              <Link
                to="/invoices"
                data-testid="home-history-link"
                aria-label="Your invoices"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 999,
                  border: `1px solid ${COLORS.rule}`,
                  backgroundColor: "transparent",
                  color: "#a8a292",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textDecoration: "none",
                  transition: "color 160ms ease, border-color 160ms ease",
                }}
              >
                <List size={15} strokeWidth={1.8} />
              </Link>
            )}
            {!isRecording && (
              <Link
                to="/settings"
                data-testid="home-settings-link"
                aria-label="Profile settings"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 999,
                  border: `1px solid ${COLORS.rule}`,
                  backgroundColor: "transparent",
                  color: "#a8a292",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textDecoration: "none",
                  transition: "color 160ms ease, border-color 160ms ease",
                }}
              >
                <SettingsIcon size={15} strokeWidth={1.8} />
              </Link>
            )}
          </div>
        )}
      </header>

      {/* Headline block */}
      <section style={{ marginTop: 56, maxWidth: 460 }}>
        <h1
          data-testid="home-headline"
          style={{
            fontFamily: "'Instrument Serif', Georgia, serif",
            fontWeight: 400,
            fontSize: 28,
            lineHeight: 1.2,
            margin: 0,
            color: "#f4f1ea",
            letterSpacing: "-0.005em",
          }}
        >
          Got a job to invoice?{" "}
          <span
            style={{
              fontStyle: "italic",
              color: COLORS.yellow,
            }}
          >
            Talk to me.
          </span>
        </h1>
      </section>

      {/* Record area */}
      <main
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 24,
          position: "relative",
        }}
      >
        {isRecording && (
          <div
            data-testid="home-timer"
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 36,
              fontWeight: 500,
              letterSpacing: "0.04em",
              color: "#f4f1ea",
              fontVariantNumeric: "tabular-nums",
              display: "flex",
              alignItems: "baseline",
              gap: 2,
            }}
          >
            <TimerWithBlink ms={elapsedMs} />
          </div>
        )}

        {isRecorded ? (
          <ProcessingView
            sizeBytes={sizeDisplay}
            durationMs={elapsedMs}
            uploading={isUploading}
            uploadError={uploadError}
            onRecordAgain={onRecordAgain}
          />
        ) : (
          <RecordButton
            recording={isRecording}
            onTap={onPrimaryTap}
          />
        )}

        {isRecording && (
          <Meter levels={levels} />
        )}

        {!isRecorded && (
          <>
            {isRecording && (
              <div
                data-testid="home-listening-indicator"
                style={{
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 11,
                  letterSpacing: "0.18em",
                  color: COLORS.red,
                  textTransform: "uppercase",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 999,
                    backgroundColor: COLORS.red,
                    boxShadow: `0 0 10px ${COLORS.red}`,
                    animation: "ledger-blink 1.1s ease-in-out infinite",
                  }}
                />
                LISTENING
              </div>
            )}
            <div
              data-testid="home-record-label"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 800,
                fontSize: 13,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: isRecording ? "#f4f1ea" : "#a8a292",
                marginTop: 6,
              }}
            >
              {isRecording ? "TAP TO STOP" : "TAP TO RECORD"}
            </div>
            <div
              data-testid="home-record-hint"
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 10,
                letterSpacing: "0.08em",
                color: "#57533f",
                marginTop: 2,
              }}
            >
              hold phone 6 inches from mouth
            </div>
          </>
        )}

        {error && (
          <div
            data-testid="home-error"
            role="alert"
            style={{
              marginTop: 12,
              padding: "10px 14px",
              borderRadius: 10,
              border: `1px solid ${COLORS.red}`,
              backgroundColor: "rgba(229,75,60,0.08)",
              color: "#ffb3ab",
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 11,
              lineHeight: 1.5,
              maxWidth: 360,
              wordBreak: "break-word",
              textAlign: "center",
            }}
          >
            {error}
          </div>
        )}
      </main>

      {/* PWA install prompt — self-hides when standalone / dismissed / desktop */}
      {isIdle && <TutorialCards />}

      {/* Indexable supporting copy — below the cards so it never crowds the
          record button, but present for crawlers and AI answer engines. */}
      {isIdle && <WhatIsHandInvoice />}

      {isIdle && <InstallPrompt />}

      {/* Structured data — rendered unconditionally so it's always in the DOM. */}
      <JsonLd data={softwareApplicationLd()} />
      <JsonLd data={howToLd()} />

      <AppFooter />
    </div>
  );
}

/* ---------------------------------- Parts ---------------------------------- */

const RecordButton = ({ recording, onTap }) => {
  const color = recording ? COLORS.recordRed : COLORS.yellow;
  return (
    <div
      style={{
        position: "relative",
        width: 180,
        height: 180,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Ripple rings while recording */}
      {recording && (
        <>
          <span
            aria-hidden
            data-testid="home-ripple-1"
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: `2px solid ${COLORS.recordRed}`,
              opacity: 0.5,
              animation: "ledger-ripple 1.8s ease-out infinite",
              pointerEvents: "none",
            }}
          />
          <span
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: `2px solid ${COLORS.recordRed}`,
              opacity: 0.4,
              animation: "ledger-ripple 1.8s ease-out 0.9s infinite",
              pointerEvents: "none",
            }}
          />
        </>
      )}

      <button
        data-testid="home-record-btn"
        aria-label={recording ? "Stop recording" : "Start recording"}
        onClick={onTap}
        style={{
          width: 180,
          height: 180,
          borderRadius: "50%",
          border: "none",
          backgroundColor: color,
          color: "#0f0e0c",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: recording
            ? `0 24px 60px -18px rgba(232,99,74,0.6)`
            : `0 24px 60px -18px rgba(232,220,45,0.55)`,
          transition: "background-color 220ms ease, box-shadow 240ms ease",
          animation: recording ? "none" : "ledger-pulse 2.4s ease-in-out infinite",
          padding: 0,
        }}
      >
        {recording ? (
          <Square size={46} strokeWidth={0} fill="#0f0e0c" data-testid="home-record-btn-stop-icon" />
        ) : (
          <Mic size={58} strokeWidth={2.2} data-testid="home-record-btn-mic-icon" />
        )}
      </button>
    </div>
  );
};

const Meter = ({ levels }) => {
  return (
    <div
      data-testid="home-meter"
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        gap: 5,
        height: 44,
        width: "100%",
        maxWidth: 260,
        pointerEvents: "none",
      }}
    >
      {levels.map((lv, i) => {
        const v = Math.min(1, Math.pow(lv, 0.7));
        const h = 3 + v * 41;
        return (
          <span
            key={i}
            data-testid={`home-meter-bar-${i}`}
            style={{
              display: "inline-block",
              width: 5,
              height: `${h}px`,
              backgroundColor: COLORS.yellow,
              borderRadius: 2.5,
              boxShadow: v > 0.15 ? `0 0 6px rgba(232,220,45,0.5)` : "none",
              transition: "height 80ms linear, box-shadow 160ms ease",
            }}
          />
        );
      })}
      {/* Pad so width stays stable when BAR_COUNT changes */}
      {Array.from({ length: Math.max(0, BAR_COUNT - levels.length) }).map((_, i) => (
        <span key={`pad-${i}`} style={{ width: 5, height: 3 }} />
      ))}
    </div>
  );
};

const TimerWithBlink = ({ ms }) => {
  const totalSec = Math.floor(ms / 1000);
  const m = String(Math.floor(totalSec / 60)).padStart(2, "0");
  const s = String(totalSec % 60).padStart(2, "0");
  const ds = String(Math.floor((ms % 1000) / 100)); // one-decimal seconds
  return (
    <>
      <span>{m}</span>
      <span
        data-testid="home-timer-blink"
        style={{ animation: "ledger-blink 1s steps(2, end) infinite" }}
      >
        :
      </span>
      <span>
        {s}.{ds}
      </span>
    </>
  );
};

const ProcessingView = ({ sizeBytes, durationMs, uploading, uploadError, onRecordAgain }) => {
  const kb = (sizeBytes / 1024).toFixed(1);
  const failed = !!uploadError;
  return (
    <div
      data-testid="home-processing"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 20,
        padding: "8px 12px",
      }}
    >
      <div
        style={{
          width: 180,
          height: 180,
          borderRadius: "50%",
          border: `1px dashed ${failed ? COLORS.red : "#3a362c"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        {uploading && (
          <span
            style={{
              position: "absolute",
              inset: 14,
              borderRadius: "50%",
              border: `1px solid ${COLORS.yellow}`,
              borderTopColor: "transparent",
              borderRightColor: "transparent",
              animation: "ledger-spin 1.2s linear infinite",
            }}
          />
        )}
        <span
          style={{
            fontFamily: "'Instrument Serif', Georgia, serif",
            fontStyle: "italic",
            fontSize: 22,
            color: failed ? COLORS.red : COLORS.yellow,
            textAlign: "center",
            padding: "0 18px",
          }}
        >
          {failed ? "Upload failed" : "Processing…"}
        </span>
      </div>
      <div
        data-testid="home-processing-meta"
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 11,
          color: "#8a8472",
          letterSpacing: "0.08em",
          textAlign: "center",
        }}
      >
        captured · {kb} KB · {formatTime(durationMs, false)}
      </div>
      {failed && (
        <div
          data-testid="home-upload-error"
          role="alert"
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: `1px solid ${COLORS.red}`,
            backgroundColor: "rgba(229,75,60,0.08)",
            color: "#ffb3ab",
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 11,
            lineHeight: 1.5,
            maxWidth: 320,
            wordBreak: "break-word",
            textAlign: "center",
          }}
        >
          {uploadError}
        </div>
      )}
      <button
        data-testid="home-record-again-btn"
        onClick={onRecordAgain}
        style={{
          padding: "12px 22px",
          borderRadius: 999,
          border: `1px solid ${COLORS.yellow}`,
          backgroundColor: "transparent",
          color: COLORS.yellow,
          fontFamily: "'Inter', sans-serif",
          fontWeight: 700,
          fontSize: 12,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          cursor: "pointer",
        }}
      >
        {failed ? "Try again" : "Record another"}
      </button>
    </div>
  );
};

const GlobalKeyframes = () => (
  <style>{`
    @keyframes ledger-pulse {
      0%   { transform: scale(1);    }
      50%  { transform: scale(1.03); }
      100% { transform: scale(1);    }
    }
    @keyframes ledger-ripple {
      0%   { transform: scale(1);   opacity: 0.55; }
      100% { transform: scale(1.6); opacity: 0;    }
    }
    @keyframes ledger-blink {
      0%, 49%   { opacity: 1; }
      50%, 100% { opacity: 0.15; }
    }
    @keyframes ledger-spin {
      to { transform: rotate(360deg); }
    }
  `}</style>
);
